import { ReactNode, useEffect, useState } from 'react';
import { Download, Printer, RefreshCw, X } from 'lucide-react';
import api, { ministriesAPI, officesAPI } from '../services/api';
import {
  downloadGrievanceReport,
  GrievanceDetail,
  printGrievanceReport,
  TimelineEntry,
  GrievanceAttachment,
  normalizeAttachments,
} from '../utils/grievanceExport';
import { toast } from 'react-hot-toast';

interface GrievanceDetailModalProps {
  grievanceId: string;
  onClose: () => void;
}

export function GrievanceDetailButton({ grievanceId }: { grievanceId: string }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 px-2 py-1 text-sm rounded-md border hover:bg-gray-50"
      >
        View / Export
      </button>
      {open && <GrievanceDetailModal grievanceId={grievanceId} onClose={() => setOpen(false)} />}
    </>
  );
}

export default function GrievanceDetailModal({ grievanceId, onClose }: GrievanceDetailModalProps) {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [grievance, setGrievance] = useState<GrievanceDetail | null>(null);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [officeName, setOfficeName] = useState<string>('');
  const [ministryName, setMinistryName] = useState<string>('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/grievances/${encodeURIComponent(grievanceId)}`);
        const data = res.data?.data || res.data;
        const timelineData = res.data?.timeline || [];
        setGrievance(data);
        setTimeline(timelineData);

        let resolvedMinistryName = data?.ministry_name || '';
        if (!resolvedMinistryName && data?.ministry_id) {
          try {
            const ministriesRes = await ministriesAPI.getAll();
            const ministry = (ministriesRes.data || []).find((m: { id: number }) => m.id === data.ministry_id);
            resolvedMinistryName = ministry?.name || '';
          } catch {
            resolvedMinistryName = '';
          }
        }
        setMinistryName(resolvedMinistryName);

        if (data?.office_id && data?.ministry_id) {
          try {
            const officesRes = await officesAPI.byMinistry(data.ministry_id);
            const office = (officesRes.data || []).find((o: { id: number }) => o.id === data.office_id);
            setOfficeName(office?.name || '');
          } catch {
            setOfficeName('');
          }
        }
      } catch {
        toast.error('Failed to load grievance details');
        onClose();
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [grievanceId, onClose]);

  const handlePrint = () => {
    if (!grievance) return;
    printGrievanceReport(grievance, timeline, officeName, ministryName);
  };

  const handleDownload = async () => {
    if (!grievance || generating) return;
    setGenerating(true);
    try {
      await downloadGrievanceReport(grievance, timeline, officeName, ministryName);
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to generate PDF');
    } finally {
      setGenerating(false);
    }
  };

  const attachments: GrievanceAttachment[] = grievance ? normalizeAttachments(grievance.attachments) : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="text-lg font-semibold">Grievance Details — {grievanceId}</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handlePrint}
              disabled={loading || !grievance}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md border hover:bg-gray-50 disabled:opacity-50"
            >
              <Printer className="h-4 w-4" /> Print
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={loading || !grievance || generating}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {generating ? (
                <><RefreshCw className="h-4 w-4 animate-spin" /> Generating…</>
              ) : (
                <><Download className="h-4 w-4" /> Download PDF</>
              )}
            </button>
            <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="text-center text-gray-500 py-8">Loading details…</div>
          ) : grievance ? (
            <>
              <Section title="Grievance Information">
                <Field label="Status" value={grievance.status} />
                <Field label="Subject" value={grievance.subject} />
                <Field label="Category" value={grievance.category} />
                <Field label="Priority" value={grievance.priority} />
                <Field label="Submitted" value={grievance.createdAt ? new Date(grievance.createdAt).toLocaleString() : undefined} />
              </Section>

              <Section title={grievance.isAnonymous ? 'Submitter Details (Anonymous)' : 'Submitter Details'}>
                {grievance.isAnonymous ? (
                  <Field label="Submission Type" value="Anonymous — personal details not provided" />
                ) : (
                  <>
                    <Field label="Full Name" value={grievance.fullName} />
                    <Field label="Email" value={grievance.email} />
                    <Field label="Phone" value={grievance.phone} />
                    <Field label="District" value={grievance.district} />
                    <Field label="Address" value={grievance.address} />
                  </>
                )}
              </Section>

              <Section title="Related Ministry / Office">
                <Field label="Ministry" value={ministryName} />
                <Field label="Office" value={officeName || (grievance.office_id ? `Office #${grievance.office_id}` : undefined)} />
              </Section>

              <Section title="Description">
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{grievance.description}</p>
              </Section>

              {attachments.length > 0 && (
                <Section title="Attachments">
                  <ul className="space-y-2 text-sm text-gray-700">
                    {attachments.map((file, i) => (
                      <li key={i}>
                        {file.url ? (
                          <a
                            href={file.url}
                            download={file.name}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 underline"
                          >
                            <Download className="h-4 w-4" />
                            {file.name}
                          </a>
                        ) : (
                          <span>{file.name}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              <Section title="Timeline">
                {timeline.length === 0 ? (
                  <p className="text-sm text-gray-500">No timeline entries</p>
                ) : (
                  <div className="space-y-3">
                    {timeline.map((item) => (
                      <div key={item.id ?? item.eventDate} className="border rounded p-3 text-sm">
                        <div className="text-gray-500">{new Date(item.eventDate).toLocaleString()}</div>
                        <div className="font-semibold capitalize">{item.status}</div>
                        <div className="whitespace-pre-wrap">{item.message}</div>
                        {item.office && <div className="text-gray-600 mt-1">{item.office}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-700 mb-2 border-b pb-1">{title}</h3>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="grid grid-cols-3 gap-2 text-sm">
      <span className="font-medium text-gray-600">{label}</span>
      <span className="col-span-2 text-gray-900">{value || '—'}</span>
    </div>
  );
}
